import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { compose } from "redux";
import { useDispatch, useSelector } from "react-redux";
import { injectIntl } from "react-intl";
import * as LiveSetupModalModes from "../constants/liveSetupModalModes";
import * as LiveSessionStates from "../constants/liveSessionStates";
import * as UserRoles from "../constants/roles";
import * as ReducerKeys from "../constants/reducerKeys";
import LoaderModal from "../../../common/LoaderModal";
import {
  LiveSessionAlert,
  LeaveSessionModal,
  LiveSessionContent,
  LiveSetupModal,
} from "../component";
import {
  canSetLocalLiveSessionStreaming,
  canSetNextLiveSessionState,
  canShowLiveFeedbackModalState,
  shouldShowBeforeUnloadAlert,
} from "../utils/liveSessionStates";
import injectMultipleReducers from "../../../utils/reducers/injectMultipleReducers";
import injectSaga from "../../../utils/saga/injectSaga";
import {
  courseReducer,
  liveSessionReducer,
  livePageReducer,
  userReducer,
  userLiveSessionReducer,
  pubnubCredentialsReducer,
  liveChannelReducer,
} from "../reducer";
import { LiveSessionProvider } from "../context/LiveSessionContext";
import { isUserCourseOnwer } from "../utils/user";
import { redirectToCourseLanding } from "../utils/pageRedirect";
import saga from "../saga";
import { ChatObservable } from "../models/ChatObservable";
import {
  useCourseActions,
  useLivePageActions,
  useLiveSessionActions,
  useLiveSessionWS,
  useBeforeUnload,
  useUserDomainActions,
  usePubnubCredentialsDomainActions,
  useLiveChannelActions,
  useUserLiveSessionDomainActions,
  useLiveSessionDomainActions,
} from "../customHooks";
import { config } from "../config";
import {
  setupHoneybadger,
  removeHoneybadger,
} from "../../../../js/utils/errorTracking/honeybadger";

const chatMessages$ = ChatObservable.create();

export const LiveSession = ({ courseId, liveSessionId, chatEnabled, liveChannelId }) => {
  const {
    loading: courseLoading,
    errorFetching: courseFetchingError,
    course,
  } = useSelector((state) => state[ReducerKeys.COURSE]);

  const {
    loading: liveSessionLoading,
    errorFetching: liveSessionFetchingError,
    errorSaving: errorSavingLiveSession,
    liveSession,
    pnWebsockets,
    streaming,
    finishedStateSuccess,
  } = useSelector((state) => state[ReducerKeys.LIVE_SESSION]);

  const {
    loading: liveChannelLoading,
    errorFetching: liveChannelFetchingError,
    liveChannel,
  } = useSelector((state) => state[ReducerKeys.LIVE_CHANNEL]);

  const {
    loading: pubnubCredentialsLoading,
    errorFetching: pubnubCredentialsFetchingError,
    pubnubCredentials,
  } = useSelector((state) => state[ReducerKeys.PUBNUB_CREDENTIALS]);

  const { loadingJWT, loadingUser, errorFetchingJWT, errorFetchingUser, jwt, user } = useSelector(
    (state) => state[ReducerKeys.USER]
  );

  const {
    loading: userLiveSessionLoading,
    errorCreating: userLiveSessionErrorCreating,
    errorFinishing: userLiveSessionErrorFinishing,
    userLiveSessionCreated,
    userLiveSessionFinished,
  } = useSelector((state) => state[ReducerKeys.USER_LIVE_SESSION]);

  const { loading: livePageLoading } = useSelector((state) => state[ReducerKeys.LIVE_PAGE]);

  const isTeacher = () => isUserCourseOnwer(course, user);

  const getUserRole = () => (isTeacher() ? UserRoles.TEACHER : UserRoles.STUDENT);

  const dispatch = useDispatch();
  const { getCourse } = useCourseActions();
  const { getPubnubCredentials } = usePubnubCredentialsDomainActions();
  const { getCurrentWithoutRelationshipsUser } = useUserDomainActions({ dispatch });
  const { getCurrentUserJWT } = useUserDomainActions();
  const { createUserLiveSession, finishUserLiveSession } = useUserLiveSessionDomainActions();
  const { setLivePageLoading } = useLivePageActions({ dispatch });
  const { getLiveSession } = useLiveSessionDomainActions();
  const { getLiveChannel } = useLiveChannelActions();
  const {
    setLiveSessionReady,
    setLocalLiveSessionStreaming,
    setLiveSessionStreaming,
    finishLiveSession,
  } = useLiveSessionActions({
    dispatch,
  });

  const {
    isSessionStreamingWSEvent,
    isSessionFinishedWSEvent,
    isSessionCancelledWSEvent,
    stateChangeOccupancy,
    sendMessage,
  } = useLiveSessionWS({
    chatMessages$,
    jwt,
    user,
    userRole: getUserRole(),
    teacher: course?.teacher,
    webSocketConfig: {
      ...(pnWebsockets || {}),
      publishKey: pubnubCredentials?.publishKey,
      subscribeKey: pubnubCredentials?.subscribeKey,
    },
  });

  const [isLeaveModalOpen, setLeaveModal] = useState(false);
  const [showPlayerLobby, setShowPlayerLobby] = useState(false);
  const [errorSendingChatMessage, setErrorSendingChatMessage] = useState();
  const [liveSetupModalState, setLiveSetupModalState] = useState({
    show: false,
    submitted: false,
    acknowledged: false,
  });

  const leaveLiveStreamingPage = useCallback(({ showLiveFeedback }) => {
    if (courseId) {
      setLivePageLoading();
      redirectToCourseLanding(courseId, showLiveFeedback);
    }
  }, []);

  const startStreaming = () => {
    if (!canSetNextLiveSessionState(liveSession.aasmState, LiveSessionStates.STREAMING)) {
      return;
    }

    setLiveSessionStreaming(liveSession.id);
  };

  const openLeaveModal = () => {
    setLeaveModal(true);
  };

  const closeLeaveModal = () => {
    setLeaveModal(false);
  };

  const onLeaveSessionHandler = () => {
    closeLeaveModal();

    if (isTeacher()) {
      finishLiveSession(liveSession.id);
    } else {
      finishUserLiveSession(liveSession.id);
    }
  };

  const onSetupModalClose = ({ liveSessionReadySubmit = false } = {}) => {
    setLiveSetupModalState({ show: false, submitted: liveSessionReadySubmit, acknowledged: true });

    if (liveSessionReadySubmit) {
      if (!canSetNextLiveSessionState(liveSession.aasmState, LiveSessionStates.READY)) {
        return;
      }

      setLiveSessionReady(liveSession?.id);
    } else if (liveSession?.aasmState === LiveSessionStates.SCHEDULED) {
      leaveLiveStreamingPage({ showLiveFeedback: false });
    }
  };

  const setupShowLobby = () => {
    if (!user?.id) {
      setShowPlayerLobby(true);
      return;
    }

    if (isTeacher() && !liveChannel?.id) {
      return;
    }

    if (isTeacher()) {
      setShowPlayerLobby(
        [LiveSessionStates.SCHEDULED, LiveSessionStates.FINISHED].indexOf(liveSession?.aasmState) >=
          0
      );
      return;
    }

    setShowPlayerLobby(
      [LiveSessionStates.SCHEDULED, LiveSessionStates.READY].indexOf(liveSession?.aasmState) >= 0
    );
  };

  const leaveCancelledLiveSession = () => {
    setShowPlayerLobby(true);
    leaveLiveStreamingPage({ showLiveFeedback: false });
  };

  const leaveFinishedLiveSession = () => {
    const leaveSession = () => {
      setShowPlayerLobby(true);
      leaveLiveStreamingPage({ showLiveFeedback: true });
    };

    if (isTeacher()) {
      leaveSession();
      return;
    }

    setTimeout(() => {
      leaveSession();
    }, config.SESSION_FINISHED_REDIRECT_TIMEOUT);
  };

  const sendChatMessage = async (message) => {
    setErrorSendingChatMessage(false);
    sendMessage({ message }).catch(() => {
      setErrorSendingChatMessage(true);
    });
  };

  const showLiveSessionAlert = () =>
    courseFetchingError ||
    liveSessionFetchingError ||
    liveChannelFetchingError ||
    errorSavingLiveSession ||
    pubnubCredentialsFetchingError ||
    errorFetchingJWT ||
    errorFetchingUser ||
    userLiveSessionErrorCreating;

  const showLoaderModal = () =>
    courseLoading ||
    liveSessionLoading ||
    liveChannelLoading ||
    pubnubCredentialsLoading ||
    loadingJWT ||
    loadingUser ||
    userLiveSessionLoading ||
    livePageLoading;

  const showLiveSessionView = () =>
    course?.id &&
    liveSession?.id &&
    pubnubCredentials?.publishKey &&
    pubnubCredentials?.subscribeKey &&
    user?.id &&
    jwt &&
    userLiveSessionCreated;

  useBeforeUnload({
    callback: (event) => {
      if (
        !isSessionCancelledWSEvent &&
        !isSessionFinishedWSEvent &&
        shouldShowBeforeUnloadAlert({
          currentState: liveSession?.aasmState,
          userRole: getUserRole(),
        })
      ) {
        event.preventDefault();
        /* eslint-disable-next-line no-param-reassign */
        event.returnValue = "";
      }
    },
  });

  useEffect(() => {
    setupHoneybadger();
    getCurrentWithoutRelationshipsUser();
    getCourse(courseId);
    getLiveSession(liveSessionId);
    getPubnubCredentials();

    return () => {
      removeHoneybadger();
    };
  }, []);

  useEffect(() => {
    if (!liveSession?.id) {
      return;
    }

    if (liveSession.isFinished && !finishedStateSuccess) {
      leaveLiveStreamingPage({ showLiveFeedback: false });
      return;
    }

    if (!userLiveSessionCreated && !userLiveSessionLoading) {
      createUserLiveSession(liveSession.id);
    }
  }, [liveSession, finishedStateSuccess]);

  useEffect(() => {
    if (user?.id && course?.id && isTeacher()) {
      getLiveChannel(liveChannelId);
    }
  }, [user, course]);

  useEffect(() => {
    if (liveSession?.id && course?.id) {
      setupShowLobby();

      if (liveChannel?.id && isTeacher() && !liveSetupModalState?.acknowledged) {
        setLiveSetupModalState((previousState) => ({ ...previousState, show: true }));
      }
    }
  }, [course, liveSession, user, liveChannel]);

  useEffect(() => {
    if (user?.id && !jwt && !loadingJWT && !errorFetchingJWT) {
      getCurrentUserJWT(user.id);
    }
  }, [user, jwt, loadingJWT, errorFetchingJWT]);

  useEffect(() => {
    if (userLiveSessionFinished || userLiveSessionErrorFinishing) {
      setShowPlayerLobby(true);
      leaveLiveStreamingPage({
        showLiveFeedback: canShowLiveFeedbackModalState(liveSession?.aasmState),
      });
    }
  }, [userLiveSessionFinished, userLiveSessionErrorFinishing]);

  useEffect(() => {
    if (isSessionCancelledWSEvent) {
      leaveCancelledLiveSession();
      return;
    }

    if (isSessionFinishedWSEvent) {
      leaveFinishedLiveSession();
      return;
    }

    if (isSessionStreamingWSEvent) {
      if (!streaming.playerToken) {
        getLiveSession(liveSessionId);
      } else if (canSetLocalLiveSessionStreaming(liveSession?.aasmState)) {
        setLocalLiveSessionStreaming();
      }
    }
  }, [isSessionStreamingWSEvent, isSessionFinishedWSEvent, isSessionCancelledWSEvent]);

  return (
    <>
      <LoaderModal isOpen={showLoaderModal()} messageKey="live_sessions.loading_message" />
      <LeaveSessionModal
        userRole={getUserRole()}
        isOpen={isLeaveModalOpen}
        onLeaveSession={onLeaveSessionHandler}
        onCancel={closeLeaveModal}
      />
      {liveSetupModalState.show && (
        <LiveSetupModal
          liveChannel={liveChannel}
          onClose={onSetupModalClose}
          mode={LiveSetupModalModes.EDITION}
          enableNavigation
        />
      )}
      {showLiveSessionAlert() && (
        <LiveSessionAlert type="error" message="generic.messages.error_try_again" top={8} />
      )}
      {showLiveSessionView() && (
        <LiveSessionProvider
          value={{
            course,
            liveChannel,
            liveSession,
            streaming,
            isTeacher: isTeacher(),
            connectedUsers: stateChangeOccupancy,
            showPlayerLobby,
            chatEnabled,
            chatMessages$,
            errorSendingChatMessage,
            onSendMessage: sendChatMessage,
            onStartStreaming: startStreaming,
            onLeaveSession: openLeaveModal,
          }}
        >
          <LiveSessionContent />
        </LiveSessionProvider>
      )}
    </>
  );
};

const withReducers = injectMultipleReducers([
  { key: ReducerKeys.COURSE, reducer: courseReducer },
  { key: ReducerKeys.LIVE_SESSION, reducer: liveSessionReducer },
  { key: ReducerKeys.LIVE_CHANNEL, reducer: liveChannelReducer },
  { key: ReducerKeys.LIVE_PAGE, reducer: livePageReducer },
  { key: ReducerKeys.USER, reducer: userReducer },
  { key: ReducerKeys.USER_LIVE_SESSION, reducer: userLiveSessionReducer },
  { key: ReducerKeys.PUBNUB_CREDENTIALS, reducer: pubnubCredentialsReducer },
]);

const withSaga = injectSaga({ key: "course[live]", saga });

export default compose(withReducers, withSaga)(injectIntl(LiveSession));

LiveSession.propTypes = {
  courseId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  liveSessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  liveChannelId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  chatEnabled: PropTypes.bool,
};

LiveSession.defaultProps = {
  chatEnabled: false,
};
